<template>
  <v-card 
    v-if="isVisible" 
    :style="sectionStyle" 
    class="mb-6 section-card" 
    elevation="0"
  >
    <v-card-title class="pb-2">
      <h3 class="text-h6 font-weight-semibold d-flex align-center mb-0" style="color: #1f2937;">
        <v-icon class="mr-2" style="color: #6b7280;">mdi-account-plus</v-icon>
        New and Re-emerging Entrants
      </h3>
    </v-card-title>
    <div class="px-4 pb-2">
      <p class="subtitle text-body mb-0" style="color: #6b7280;">Suppliers in your industry that won their first contract ever or returned after 12+ months</p>
    </div>

    <div v-if="isLoading" class="text-center py-8">
      <v-progress-circular indeterminate color="#ff1463"></v-progress-circular>
    </div>

    <v-card-text v-else-if="!entrants || entrants.length === 0" class="text-center py-8 grey--text">
      No new or re-emerging entrants found
    </v-card-text>

    <v-card-text v-else class="pa-0">
      <div 
        v-for="(entry, index) in paginatedEntrants" 
        :key="entry.tender_id || index"
        class="entry-card pa-6"
        :class="{ 'entry-card-border-top': index > 0 }"
        @click="handleEntryClick(entry)"
      >
        <div class="d-flex justify-space-between align-start">
          <div class="flex-grow-1">
            <div class="d-flex align-center gap-2 mb-2">
              <v-chip 
                v-if="entry.entry_type === 'first_ever'" 
                x-small 
                style="background-color: #10b981; color: white; font-weight: 600; height: 22px; font-size: 11px;"
              >
                First Contract
              </v-chip>
              <v-chip 
                v-else-if="entry.entry_type === 're_emerging'" 
                x-small 
                style="background-color: #3b82f6; color: white; font-weight: 600; height: 22px; font-size: 11px;"
              >
                Re-emerging
              </v-chip>
              <v-chip 
                v-if="entry.matching_industry" 
                x-small 
                style="background-color: #e6f0f5; color: #12253a; font-weight: 500; height: 22px; font-size: 11px;"
              >
                {{ entry.matching_industry }}
              </v-chip>
            </div>
            
            <div class="text-body-2 mb-2">
              <span class="font-weight-semibold">
                <router-link 
                  v-if="entry.supplier_id" 
                  :to="`/supplier/${entry.supplier_id}`" 
                  class="text-decoration-none" 
                  style="color: inherit;"
                  target="_blank"
                >
                  {{ entry.supplier_name }}
                </router-link>
                <span v-else>{{ entry.supplier_name }}</span>
              </span>
              <span class="grey--text"> won a contract</span>
            </div>

            <router-link 
              :to="`/tender/${entry.tender_id}`" 
              class="text-h6 font-weight-medium text-decoration-none d-flex align-center mb-3"
              style="color: #1f2937;"
              target="_blank"
            >
              {{ entry.title }}
              <v-icon small class="ml-2 grey--text">mdi-open-in-new</v-icon>
            </router-link>

            <div class="d-flex align-center gap-4 text-body-2 grey--text mb-3">
              <span class="d-flex align-center">
                <v-icon small class="mr-1">mdi-map-marker</v-icon>
                <router-link 
                  v-if="entry.publisher_id" 
                  :to="`/entity/${entry.publisher_id}`" 
                  class="text-decoration-none" 
                  style="color: inherit;"
                  target="_blank"
                >
                  {{ entry.publisher_name }}
                </router-link>
                <span v-else>{{ entry.publisher_name }}</span>
              </span>
              <span class="d-flex align-center">
                <v-icon small class="mr-1">mdi-cash</v-icon>
                {{ formatCurrency(entry.value || 0) }}
              </span>
              <span v-if="entry.contract_start" class="d-flex align-center">
                <v-icon small class="mr-1">mdi-calendar</v-icon>
                {{ formatDate(entry.contract_start) }}
              </span>
              <v-chip v-if="entry.category" label x-small color="secondary" text-color="white">
                {{ entry.category }}
              </v-chip>
            </div>
          </div>

          <v-btn
            small
            depressed
            dark
            class="ml-4 body-2 text-capitalize"
            color="#10b981"
            text-color="white"
            style="font-size: 13px;"
            :to="`/tender/${entry.tender_id}`"
            target="_blank"
          >
            <v-icon small left>mdi-eye</v-icon>
            View
          </v-btn>
        </div>
      </div>

      <!-- Pagination -->
      <v-card-actions 
        v-if="showPagination" 
        class="pa-4" 
        style="border-top: 1px solid #e5e7eb; background-color: #f9fafb;"
      >
        <div class="d-flex justify-space-between align-center" style="width: 100%;">
          <div class="text-body" style="color: #6b7280;">
            Showing {{ paginationStart }} - {{ paginationEnd }} of {{ entrants.length }}
          </div>
          <div class="d-flex gap-2">
            <v-btn
              icon
              x-small
              :disabled="currentPage === 1"
              @click="goToPreviousPage"
              style="color: #6b7280;"
            >
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn
              icon
              x-small
              :disabled="currentPage >= totalPages"
              @click="goToNextPage"
              style="color: #6b7280;"
            >
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card-actions>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'NewEntrantsSection',
  
  props: {
    entrants: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    sectionStyle: {
      type: String,
      default: ''
    },
    isVisible: {
      type: Boolean,
      default: true
    },
    itemsPerPage: {
      type: Number,
      default: 10
    }
  },

  data() {
    return {
      currentPage: 1
    };
  },

  computed: {
    paginatedEntrants() {
      if (!this.entrants || this.entrants.length === 0) return [];
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.entrants.slice(start, end);
    },

    totalPages() {
      if (!this.entrants || this.entrants.length === 0) return 0;
      return Math.ceil(this.entrants.length / this.itemsPerPage);
    },

    showPagination() {
      return this.entrants && this.entrants.length > this.itemsPerPage;
    },

    paginationStart() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },

    paginationEnd() {
      return Math.min(this.currentPage * this.itemsPerPage, this.entrants.length);
    }
  },

  watch: {
    entrants() {
      // Reset to first page when entrants change
      this.currentPage = 1;
    }
  },

  methods: {
    handleEntryClick(entry) {
      if (entry.tender_id) {
        this.$router.push(`/tender/${entry.tender_id}`);
      }
    },

    goToPreviousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    goToNextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    formatCurrency(value) {
      if (!value) return "$0";
      if (value >= 1000000) {
        return `$${(value / 1000000).toFixed(1)}M`;
      } else if (value >= 1000) {
        return `$${(value / 1000).toFixed(0)}K`;
      }
      return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      } catch (e) {
        return String(dateStr);
      }
    }
  }
};
</script>

<style scoped>
.entry-card {
  cursor: pointer;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f0fdf4;
  border-left: 3px solid #10b981;
  transition: background-color 0.2s ease;
}

.entry-card:hover {
  background-color: #e8f5e9;
}

.entry-card-border-top {
  border-top: 1px solid #e5e7eb;
}
</style>
